import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { mediatorAPI } from '../../services/api';
import Button from '../../components/common/Button';
import Icon from '../../components/common/Icon';

const CreateMediator = () => {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset
  } = useForm({
    defaultValues: {
      active: true
    }
  });

  const onSubmit = async (data) => {
    setSubmitting(true);
    
    try {
      // Prepare mediator data matching the curl structure
      const mediatorData = {
        mediatorName: data.mediatorName,
        phoneNumber: data.phoneNumber,
        mediatorCode: data.mediatorCode,
        active: data.active,
        email: data.email,
        bankDetails: data.bankDetails || null
      };

      const response = await mediatorAPI.createMediator(mediatorData);
      
      toast.success('Mediator created successfully!');
      reset();
      
      // Navigate back to mediators list after a short delay
      setTimeout(() => {
        navigate('/admin/mediators');
      }, 1500);
      
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to create mediator';
      toast.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleBack = () => {
    navigate('/admin/mediators');
  };

  return (
    <div style={{ 
      padding: '24px',
      flex: 1,
      backgroundColor: '#f8fafc',
      display: 'flex',
      flexDirection: 'column',
      width: '100%',
      overflow: 'auto',
      position: 'relative',
      minHeight: 0
    }}>
      <div style={{ maxWidth: '800px', margin: '0 auto', width: '100%' }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '16px',
          marginBottom: '32px'
        }}>
          <button
            onClick={handleBack}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '8px 16px',
              background: '#ffffff',
              border: '1px solid var(--border)',
              borderRadius: '8px',
              color: 'var(--text-sub)',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            <Icon name="arrow-left" size={16} />
            Back
          </button>
          
          <div>
            <h1 style={{
              fontFamily: 'Outfit, sans-serif',
              fontWeight: '700',
              fontSize: '24px',
              color: 'var(--text)',
              margin: 0
            }}>
              Create Mediator
            </h1>
            <p style={{
              fontSize: '14px',
              color: 'var(--text-muted)',
              margin: '4px 0 0 0'
            }}>
              Add a new mediator to the system
            </p>
          </div>
        </div>

        {/* Form Card */}
        <div style={{ 
          background: '#ffffff', 
          borderRadius: '16px', 
          padding: '32px', 
          border: '1px solid #e2e8f0', 
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
          marginBottom: '24px'
        }}>
          <form onSubmit={handleSubmit(onSubmit)} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            {/* Mediator Name */}
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '600', color: 'var(--text)' }}>
                Mediator Name *
              </label>
              <input
                {...register('mediatorName', { 
                  required: 'Mediator name is required',
                  minLength: { 
                    value: 2, 
                    message: 'Mediator name must be at least 2 characters' 
                  }
                })}
                type="text"
                placeholder="Enter mediator name"
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid var(--border)',
                  borderRadius: '8px',
                  backgroundColor: '#ffffff',
                  color: 'var(--text)',
                  fontSize: '14px',
                  borderColor: errors.mediatorName ? 'var(--red)' : 'var(--border)'
                }}
              />
              {errors.mediatorName && (
                <p style={{ color: 'var(--red)', fontSize: '12px', marginTop: '4px' }}>
                  {errors.mediatorName.message}
                </p>
              )}
            </div>

            {/* Phone Number and Mediator Code */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '600', color: 'var(--text)' }}>
                  Phone Number *
                </label>
                <div style={{ display: 'flex' }}>
                  <span style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '0 12px',
                    background: '#f8fafc',
                    border: '1px solid var(--border)',
                    borderRight: 'none',
                    borderRadius: '8px 0 0 8px',
                    fontSize: '14px',
                    color: 'var(--text-sub)'
                  }}>+91</span>
                  <input
                    {...register('phoneNumber', { 
                      required: 'Phone number is required',
                      pattern: {
                        value: /^[0-9]{10}$/,
                        message: 'Phone number must be exactly 10 digits'
                      }
                    })}
                    type="tel"
                    placeholder="9876543210"
                    maxLength="10"
                    style={{
                      flex: 1,
                      padding: '12px',
                      border: '1px solid var(--border)',
                      borderRadius: '0 8px 8px 0',
                      backgroundColor: '#ffffff',
                      color: 'var(--text)',
                      fontSize: '14px',
                      borderColor: errors.phoneNumber ? 'var(--red)' : 'var(--border)'
                    }}
                  />
                </div>
                {errors.phoneNumber && (
                  <p style={{ color: 'var(--red)', fontSize: '12px', marginTop: '4px' }}>
                    {errors.phoneNumber.message}
                  </p>
                )}
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '600', color: 'var(--text)' }}>
                  Mediator Code *
                </label>
                <input
                  {...register('mediatorCode', { 
                    required: 'Mediator code is required',
                    pattern: {
                      value: /^[A-Z0-9-_]+$/,
                      message: 'Mediator code can only contain uppercase letters, numbers, hyphens, and underscores'
                    }
                  })}
                  type="text"
                  placeholder="MED001"
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid var(--border)',
                    borderRadius: '8px',
                    backgroundColor: '#ffffff',
                    color: 'var(--text)',
                    fontSize: '14px',
                    borderColor: errors.mediatorCode ? 'var(--red)' : 'var(--border)'
                  }}
                />
                {errors.mediatorCode && (
                  <p style={{ color: 'var(--red)', fontSize: '12px', marginTop: '4px' }}>
                    {errors.mediatorCode.message}
                  </p>
                )}
              </div>
            </div>

            {/* Email */}
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '600', color: 'var(--text)' }}>
                Email Address
              </label>
              <input
                {...register('email', { 
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: 'Please enter a valid email address'
                  }
                })}
                type="email"
                placeholder="mediator@example.com"
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid var(--border)',
                  borderRadius: '8px',
                  backgroundColor: '#ffffff',
                  color: 'var(--text)',
                  fontSize: '14px',
                  borderColor: errors.email ? 'var(--red)' : 'var(--border)'
                }}
              />
              {errors.email && (
                <p style={{ color: 'var(--red)', fontSize: '12px', marginTop: '4px' }}>
                  {errors.email.message}
                </p>
              )}
            </div>

            {/* Bank Details */}
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '600', color: 'var(--text)' }}>
                Bank Details
              </label>
              <textarea
                {...register('bankDetails')}
                placeholder="Enter bank details (optional)"
                rows={3}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid var(--border)',
                  borderRadius: '8px',
                  backgroundColor: '#ffffff',
                  color: 'var(--text)',
                  fontSize: '14px',
                  resize: 'vertical',
                  fontFamily: 'inherit',
                  borderColor: errors.bankDetails ? 'var(--red)' : 'var(--border)'
                }}
              />
              {errors.bankDetails && (
                <p style={{ color: 'var(--red)', fontSize: '12px', marginTop: '4px' }}>
                  {errors.bankDetails.message}
                </p>
              )}
            </div>

            {/* Active Status */}
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '600', color: 'var(--text)' }}>
                Status
              </label>
              <select
                {...register('active')}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid var(--border)',
                  borderRadius: '8px',
                  backgroundColor: '#ffffff',
                  color: 'var(--text)',
                  fontSize: '14px',
                  borderColor: errors.active ? 'var(--red)' : 'var(--border)'
                }}
              >
                <option value={true}>Active</option>
                <option value={false}>Inactive</option>
              </select>
              {errors.active && (
                <p style={{ color: 'var(--red)', fontSize: '12px', marginTop: '4px' }}>
                  {errors.active.message}
                </p>
              )}
            </div>

            {/* Form Actions */}
            <div style={{ 
              display: 'flex', 
              gap: '12px', 
              justifyContent: 'flex-end',
              paddingTop: '20px',
              borderTop: '1px solid var(--border)'
            }}>
              <Button
                type="button"
                onClick={handleBack}
                variant="secondary"
                disabled={submitting}
                style={{
                  padding: '12px 24px',
                  fontSize: '14px',
                  fontWeight: '600',
                  borderRadius: '8px',
                  flex: 1
                }}
              >
                Cancel
              </Button>

              <Button
                type="submit"
                disabled={submitting}
                loading={submitting}
                style={{
                  padding: '12px 24px',
                  fontSize: '14px',
                  fontWeight: '600',
                  borderRadius: '8px',
                  flex: 1
                }}
              >
                {submitting ? 'Creating...' : 'Create Mediator'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CreateMediator;
